import * as React from "react"
import { cn } from "@/utils"
import { Button } from "./button"
import { ArrowLeft } from "lucide-react"

export interface DetailHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  title: string
  subtitle?: string
  backUrl?: string
  onBack?: () => void
  actions?: {
    label: string
    icon?: React.ReactNode
    onClick: () => void
    variant?: "default" | "destructive" | "outline" | "secondary" | "ghost" | "link"
    disabled?: boolean
  }[]
  status?: {
    label: string
    variant: "default" | "destructive" | "success" | "warning" | "info"
  }
  breadcrumbs?: {
    label: string
    href?: string
  }[]
}

const DetailHeader = React.forwardRef<HTMLDivElement, DetailHeaderProps>(
  ({
    className,
    title,
    subtitle,
    backUrl,
    onBack,
    actions = [],
    status,
    breadcrumbs = [],
    ...props
  }, ref) => {
    const handleBack = () => {
      if (onBack) {
        onBack()
      } else if (backUrl) {
        window.location.href = backUrl
      } else {
        window.history.back()
      }
    }

    const getStatusClasses = (variant: string) => {
      switch (variant) {
        case "success":
          return "bg-green-100 text-green-800 border-green-200"
        case "warning":
          return "bg-yellow-100 text-yellow-800 border-yellow-200"
        case "destructive":
          return "bg-red-100 text-red-800 border-red-200"
        case "info":
          return "bg-blue-100 text-blue-800 border-blue-200"
        default:
          return "bg-gray-100 text-gray-800 border-gray-200"
      }
    }

    return (
      <div
        ref={ref}
        className={cn(
          "bg-white border-b border-gray-200 px-6 py-4",
          className
        )}
        {...props}
      >
        <div className="flex flex-col space-y-4">
          {/* Breadcrumbs */}
          {breadcrumbs.length > 0 && (
            <div className="flex items-center space-x-2 text-sm text-gray-500">
              {breadcrumbs.map((crumb, index) => (
                <React.Fragment key={index}>
                  {crumb.href ? (
                    <a
                      href={crumb.href}
                      className="hover:text-gray-700 transition-colors"
                    >
                      {crumb.label}
                    </a>
                  ) : (
                    <span>{crumb.label}</span>
                  )}
                  {index < breadcrumbs.length - 1 && (
                    <span className="text-gray-300">/</span>
                  )}
                </React.Fragment>
              ))}
            </div>
          )}

          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-4">
              {/* Back button */}
              {(backUrl || onBack) && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleBack}
                  className="h-10 w-10 rounded-full"
                  aria-label="Retour"
                >
                  <ArrowLeft className="h-5 w-5" />
                </Button>
              )}

              <div className="space-y-1">
                <div className="flex items-center space-x-3">
                  <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
                  {status && (
                    <span
                      className={cn(
                        "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border",
                        getStatusClasses(status.variant)
                      )}
                    >
                      {status.label}
                    </span>
                  )}
                </div>
                {subtitle && (
                  <p className="text-sm text-gray-500">{subtitle}</p>
                )}
              </div>
            </div>

            {/* Actions */}
            {actions.length > 0 && (
              <div className="flex items-center space-x-2">
                {actions.map((action, index) => (
                  <Button
                    key={index}
                    variant={action.variant || "outline"}
                    onClick={action.onClick}
                    disabled={action.disabled}
                    className="flex items-center space-x-2"
                  >
                    {action.icon}
                    <span>{action.label}</span>
                  </Button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }
)
DetailHeader.displayName = "DetailHeader"

export { DetailHeader }